import {useTranslation} from 'react-i18next';
import {getListClassNames} from 'styles';
import React from 'react';
import {List, Stack, useTheme, Text} from '@fluentui/react';
import {LoadWrapper} from 'components/LoadWrapper';
import {WorkbenchHeader} from '../Headers';
import {BaseLineHeaderRow, DashboardCardProps} from '..';
import {sortObjectsByDate} from 'helpers/sortHelpers';
import {useDashboardQueries} from 'features/Dashboard/hooks/useDashboardQueries';
import {GetWorkbenchBaselinesQuery} from 'features/Dashboard/hooks/workbenchItems.generated';
import {Link} from 'react-router-dom';
import i18n from 'i18n/i18n';
import {formatDateTime} from 'i18n/localeDateFormat';

type BaseLineItem = GetWorkbenchBaselinesQuery['workbenchBaselines'][0];

export const BaseLineItems: React.FC<DashboardCardProps> = ({
	halfHeight = false,
	itemCountLoaded,
}) => {
	const {t} = useTranslation('features/dashboard', {
		keyPrefix: 'BedarfsWorkbenchItems',
	});

	const {getBaseLineItems} = useDashboardQueries();
	const {data, loading} = getBaseLineItems();
	const theme = useTheme();
	const classNames = getListClassNames(theme, halfHeight);

	const allBaseLines = React.useMemo(() => {
		return sortObjectsByDate(data?.workbenchBaselines ?? [], 'createdAt');
	}, [data]);

	React.useEffect(() => {
		if (!loading && data?.workbenchBaselines)
			itemCountLoaded?.(data?.workbenchBaselines?.length);
	}, [itemCountLoaded, data, loading]);

	const onRenderCell = (item?: BaseLineItem): JSX.Element => {
		if (!item) return <></>;
		const vehicleProjectName = item.modelSeries + ' ' + item.generation;
		const vehicleParts = vehicleProjectName?.split(' ');
		const modelCode = vehicleParts?.pop();
		const projectName = vehicleParts?.join(' ') ?? '';

		const url = `/vehicleProjects/${encodeURIComponent(
			projectName,
		)}/${modelCode}/baselines/${item.id}`;

		return (
			<Link to={url} style={{textDecoration: 'none'}}>
				<div className={classNames.itemCell} data-is-focusable={true}>
					<div className={classNames.itemContent}>
						<Stack
							horizontal
							tokens={{childrenGap: 16}}
							horizontalAlign='start'
						>
							<Stack.Item grow={3}>
								<div style={{color: theme.palette.neutralDark}}>
									{vehicleProjectName}
								</div>
							</Stack.Item>
							<Stack.Item grow={1} style={{textAlign: 'right'}}>
								<Text className={classNames.itemCountInline}>
									{formatDateTime(new Date(item.createdAt), i18n)}
								</Text>
							</Stack.Item>
						</Stack>
					</div>
				</div>
			</Link>
		);
	};

	return (
		<div>
			<WorkbenchHeader
				halfHeight
				title={t('TileHeader')}
				items={allBaseLines.length}
			/>
			<div className={classNames.container} data-is-scrollable>
				<BaseLineHeaderRow t={t} />
				<LoadWrapper loading={loading}>
					<List
						items={allBaseLines}
						onRenderCell={item => onRenderCell(item)}
					/>
				</LoadWrapper>
			</div>
		</div>
	);
};
